import { useState, useEffect, useMemo, useCallback } from 'react'
import type { ValueBetItem } from '../types'
import { ValueBetCard } from './ValueBetCard'
import { ValueBetsTable } from './ValueBetsTable'
import { ValueBetsFilterBar, type ValueBetsFilters } from './ValueBetsFilterBar'
import { DroppingOddsSection } from './DroppingOddsSection'
import { SkeletonValueBetCard } from './Skeleton'
import { IoRefresh } from 'react-icons/io5'
import { useValueBets } from '../swr-config'
import { useFeatureTracking, FeatureNames } from '../hooks/useActivityTracking'

const DEFAULT_FILTERS: ValueBetsFilters = {
  minOdds: null,
  maxOdds: null,
  minEdge: 0, // Only show positive edge by default
  maxEdge: null,
  marketCategories: [],
  leagueIds: [],
  fixtureId: null,
  sortBy: 'edge',
  sortOrder: 'desc',
  daysAhead: 7,
}

const STORAGE_KEY = 'statscout_value_bets_filters'
const LIMIT = 50

function loadFiltersFromStorage(): ValueBetsFilters {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Merge with defaults to handle any missing keys from older versions
      return { ...DEFAULT_FILTERS, ...parsed }
    }
  } catch {
    // Invalid JSON or localStorage error - use defaults
  }
  return DEFAULT_FILTERS
}

export function ValueBetsPage() {
  // Track feature usage
  useFeatureTracking(FeatureNames.VALUE_BETS)

  const [filters, setFiltersState] = useState<ValueBetsFilters>(loadFiltersFromStorage)

  // Persist filters to localStorage
  const setFilters = useCallback((newFilters: ValueBetsFilters | ((prev: ValueBetsFilters) => ValueBetsFilters)) => {
    setFiltersState((prev) => {
      const resolved = typeof newFilters === 'function' ? newFilters(prev) : newFilters
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(resolved))
      } catch {
        // localStorage full or unavailable - continue without persistence
      }
      return resolved
    })
  }, [])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [page, setPage] = useState(0)

  // Build SWR params from filters
  const swrParams = useMemo(() => ({
    minOdds: filters.minOdds,
    maxOdds: filters.maxOdds,
    minEdge: filters.minEdge,
    maxEdge: filters.maxEdge,
    marketCategories: filters.marketCategories,
    leagueIds: filters.leagueIds,
    fixtureId: filters.fixtureId,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    limit: LIMIT,
    offset: page * LIMIT,
    daysAhead: filters.daysAhead,
  }), [filters, page])

  // SWR hook with automatic caching, deduplication, and focus refetch
  const { data, error, isLoading, mutate } = useValueBets(swrParams)

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [
    filters.minOdds,
    filters.maxOdds,
    filters.minEdge,
    filters.maxEdge,
    filters.marketCategories,
    filters.leagueIds,
    filters.fixtureId,
    filters.sortBy,
    filters.sortOrder,
    filters.daysAhead,
  ])

  // Generate unique key for expanded state
  const getItemKey = (item: ValueBetItem) =>
    `${item.fixture_id}-${item.market_type}-${item.selection_label}-${item.outcome}`

  const handleToggle = (item: ValueBetItem) => {
    const key = getItemKey(item)
    setExpandedId((prev) => (prev === key ? null : key))
  }

  const totalPages = data ? Math.ceil(data.total_count / LIMIT) : 0
  const errorMessage = error ? (error instanceof Error ? error.message : String(error)) : null

  return (
    <div className="value-bets-page">
      <div className="card">
        <div className="card-inner">
          <div className="page-header">
            <div className="page-header-main">
              <h1>Best Value Bets</h1>
              <button
                className="refresh-btn"
                onClick={() => mutate()}
                disabled={isLoading}
                title="Refresh"
              >
                <IoRefresh size={18} className={isLoading ? 'spinning' : ''} />
              </button>
            </div>
            <p className="page-subtitle">
              Bets where our probability exceeds the bookmaker's implied probability
            </p>
          </div>

          <ValueBetsFilterBar filters={filters} onChange={setFilters} />

          {/* Dropping odds section - shows significant odds movements */}
          <DroppingOddsSection
            leagueIds={filters.leagueIds.length > 0 ? filters.leagueIds : undefined}
            limit={5}
            collapsedByDefault={true}
          />

          {isLoading && !data && (
            <div className="skeleton-loading-container">
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonValueBetCard key={i} />
              ))}
            </div>
          )}

          {errorMessage && (
            <div className="error-state">
              <p>Error loading value bets: {errorMessage}</p>
              <button onClick={() => mutate()}>Retry</button>
            </div>
          )}

          {!errorMessage && data && (
            <>
              <div className="results-summary">
                Showing {data.items.length} of {data.total_count} value bets
              </div>

              {/* Table view for larger screens */}
              <ValueBetsTable
                items={data.items}
                expandedId={expandedId}
                onToggle={handleToggle}
                getItemKey={getItemKey}
              />

              {/* Card view for mobile */}
              <div className="value-bets-list mobile-only">
                {data.items.map((item) => (
                  <ValueBetCard
                    key={getItemKey(item)}
                    bet={item}
                    isExpanded={expandedId === getItemKey(item)}
                    onToggle={() => handleToggle(item)}
                  />
                ))}
              </div>

              {data.items.length === 0 && (
                <div className="empty-state">
                  <p>No value bets match your filters.</p>
                  <p>Try adjusting your filters or check back later.</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                    Previous
                  </button>
                  <span>
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
